<template>
  <div id="page-product-bundle-form">
    <vs-row>
      <vs-col v-if="hasError">
        <vs-alert color="danger" title="Error" class="mb-4">
          <span>The selected Bundle no longer exists. Please select a new one.</span>
        </vs-alert>
      </vs-col>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <card-page
            :card-title="cardTitle"
            :switch-model="model.IsEnabled"
        >
          <vue-element-loading :active="busy || formRatersBusy"></vue-element-loading>
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
          />
          <vue-form-generator
              v-if="!formRatersBusy"
              ref="formRaters"
              :model="model"
              :options="formOptions"
              :schema="schemaRaters"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import CardPage from '../../components/Views/CardPage';
import moment from 'moment';
import { selectOptions } from '@/components/form/FormElementOption';
import NotificationMixin from "@/components/mixins/NotificationMixin";

export default {
  name: 'ProductForm',
  mixins: [ NotificationMixin ],
  components: {
    CardPage,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  mixins: [FormModelMixin],
  data() {
    return {
      formReferences: ['form', 'formRaters'],
      formRatersBusy: false,
      selectedReferences: [],
      hasError: false,
      model: {
        main_raters: {},
      },
      schemaRaters: {
        groups: []
      },
      schema: {
        groups: []
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      oraOnlineRaterBundles: [],
      products: [],
      productsCrossSelling: [],
      options: [
        {
          endpoint: () => this.getBundles(),
          optionsFieldName: 'oraOnlineRaterBundles',
          nameFieldName: 'ORABundleName',
          idFieldName: 'ORABundle_FK',
          externalIdFieldName: 'ORABundle_PK',
        },
        {
          endpoint: () => this.$api.products.list(),
          optionsFieldName: 'products',
          nameFieldName: 'ProductCode',
          idFieldName: 'Product_FK',
          modelArrayName: 'main_raters'
        },
        {
          endpoint: () => this.$api.products.list(),
          optionsFieldName: 'productsCrossSelling',
          nameFieldName: 'ProductCode',
          idFieldName: 'Product_FK',
          modelArrayName: 'main_raters.cross_selling'
        },
      ]
    };
  },
  watch: {
    model(value) {
      if (typeof value.ORABundle_FK !== 'object') {
        this.getRatersFromBundle(value.ORABundle_FK);
      }
    },
    hasError() {
      this.model.ORABundle_FK = null;
      this.setSchemaGroups();
    }
  },
  mounted() {
    this.setSchemaGroups();
  },
  methods: {
    setSchemaGroups() {
    this.schema.groups = [];

    this.schema.groups.push(
      {
        legend: 'General',
        styleClasses: 'w-full',
        fields: [
          {
            disabled: this.readonly,
            type: 'input',
            inputType: 'text',
            styleClasses: 'w-1/3',
            label: 'Bundle Name',
            model: 'OrionBundleName',
            required: !this.readonly,
            validator: ['string', 'required'],
          },
        ]
      },
      {
        legend: 'Online Rater',
        styleClasses: 'w-full',
        fields: [
          {
            disabled: this.readonly,
            type: 'vueMultiSelect',
            inputType: 'text',
            styleClasses: 'w-1/2',
            label: 'Online Rater Bundle',
            model: 'ORABundle_FK',
            required: !this.readonly,
            validator: 'selectRequired',
            values: () => this.oraOnlineRaterBundles,
            onChanged: (model) => {
              model.ORABundleName = model.ORABundle_FK.name;
              this.getRatersFromBundle(model.ORABundle_FK.id, true);
            },
            selectOptions,
          },
        ]
      }
    ); 
    },
    async getBundles() {
      let bundles = {data:{data:[]}};

    try {
      bundles = await this.$api.productBundles.getBundles()
    } catch (e) {
      console.error(e)
      this.notifyError('Error fetching Bundles');
      this.hasError = true;
    }

      return bundles;
    },
    async getRatersFromBundle(selectedBundle, resetModel = false) {
      this.formRatersBusy = true;
      this.schemaRaters.groups = [];

      if (!selectedBundle) {
        this.formRatersBusy = false;
        return;
      }

      this.hasError = false;

      try {
        const { data } = await this.$api.productBundles.getRatersFromBundle(selectedBundle);
        const oraRatersFromBundles = data.data;

        if (resetModel) {
          this.model.main_raters = oraRatersFromBundles.map(i => {
            return {
              ExpiryDate: i.ExpiryDate,
              FacilityReference: i.FacilityReference,
              FacilitySection: i.FacilitySection,
              InceptionDate: i.InceptionDate,
              ORABundle_FK: i.ORABundle_PK,
              ORAMainRaterName: i.ORAMainRaterName,
              ORAMainRater_FK: i.ORAMainRater_FK,
              cross_selling: i.CrossSellingList,
            };
          });
        }

        if (oraRatersFromBundles.length) {
          oraRatersFromBundles.forEach((bundle, raterIndex) => {
            const inceptionDate = moment(bundle.InceptionDate).format('DD/MM/YYYY');
            const expiryDate = moment(bundle.ExpiryDate).format('DD/MM/YYYY');
            
            let mainRater = {
              model: 'main_raters',
              legend: `Main Rater (${inceptionDate} - ${expiryDate})`,
              styleClasses: 'w-full main_rater',
              fields: [
                {
                  type: "label",
                  styleClasses: "w-1/2",
                  label: "Main Rater Name",
                  model: `main_raters.${raterIndex}.ORAMainRaterName`,
                },
                {
                  disabled: this.readonly || this.hasError,
                  type: 'vueMultiSelect',
                  inputType: 'text',
                  styleClasses: 'w-1/2',
                  label: 'Select Product',
                  model: `main_raters.${raterIndex}.Product_FK`,
                  required: !this.readonly,
                  validator: 'selectRequired',
                  values: () => this.products,
                  selectOptions,
                },
              ]
            };
            
            this.schemaRaters.groups.push(mainRater);

            let crossSellingRaterFields = [];
            if (bundle.CrossSellingList.length) {
              bundle.CrossSellingList.forEach( (crossSelling, crossSellingIndex) => {
                crossSellingRaterFields.push(
                  {
                    type: "label",
                    styleClasses: "w-1/2",
                    label: 'Cross Selling Name',
                    model: `main_raters.${raterIndex}.cross_selling.${crossSellingIndex}.ORACrossSellingRaterName`,
                  },
                  {
                    disabled: this.readonly || this.hasError,
                    type: 'vueMultiSelect',
                    inputType: 'text',
                    styleClasses: 'w-1/2',
                    label: 'Select Product',
                    model: `main_raters.${raterIndex}.cross_selling.${crossSellingIndex}.Product_FK`,
                    required: !this.readonly,
                    validator: 'selectRequired',
                    values: () => this.productsCrossSelling,
                    selectOptions,
                  },
                );
              });

              this.schemaRaters.groups.push({
                legend: 'Cross Selling Raters',
                styleClasses: 'w-full',
                fields: crossSellingRaterFields,
              })
            }
          });
        }
      } catch (e) {
        console.error(e)
        this.notifyError('Bundle not found');
        this.hasError = true;
      }

        this.formRatersBusy = false;
    }
  }
};
</script>

<style lang="scss">
.main_rater {
  > legend {
    border-top: 15px solid #eeeeee !important;
  }
}
</style>
